// Viewport state — pan + zoom for the current view. Not part of the Document
// because zoom/pan are per-view and not undoable.

export interface Viewport {
  readonly panX: number; // screen-space offset where doc (0,0) lands
  readonly panY: number;
  readonly zoom: number; // 1 = 100%
}

export function screenToDoc(v: Viewport, sx: number, sy: number): { x: number; y: number } {
  return { x: (sx - v.panX) / v.zoom, y: (sy - v.panY) / v.zoom };
}

export function docToScreen(v: Viewport, dx: number, dy: number): { x: number; y: number } {
  return { x: dx * v.zoom + v.panX, y: dy * v.zoom + v.panY };
}

// Zoom while keeping the screen point (sx, sy) fixed in document space.
export function zoomAt(v: Viewport, factor: number, sx: number, sy: number): Viewport {
  const newZoom = clamp(v.zoom * factor, 0.01, 64);
  const doc = screenToDoc(v, sx, sy);
  return {
    zoom: newZoom,
    panX: sx - doc.x * newZoom,
    panY: sy - doc.y * newZoom,
  };
}

export function fitToScreen(
  docWidth: number,
  docHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  padding = 32,
): Viewport {
  // Photoshop's Fit on Screen zooms up to fit a small image, not just down —
  // a 32×32 favicon should fill the viewport, not display tiny.
  const zoom = Math.min(
    64,
    (viewportWidth - padding * 2) / docWidth,
    (viewportHeight - padding * 2) / docHeight,
  );
  return {
    zoom,
    panX: (viewportWidth - docWidth * zoom) / 2,
    panY: (viewportHeight - docHeight * zoom) / 2,
  };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
